import { formatTextWithEllipsis } from './smartTextFormatting';

/**
 * Smart genre formatting that fits as many genres as possible within character constraints
 * @param {Array} workTypes - Array of workType objects with optional displayStatus and workType.name
 * @param {number} maxChars - Maximum number of characters allowed (default: 18)
 * @param {boolean} filterByDisplayStatus - Whether to filter by displayStatus=2 (default: true for profiles, false for organizations)
 * @returns {string} Formatted genre string with appropriate truncation and count
 */
export const formatGenres = (workTypes = [], maxChars = 16) => {

  const genreNames = workTypes?.filter(wt => wt.displayStatus === 2)
    .map(wt => wt.workType?.name)
    .filter(Boolean);

  return formatTextWithEllipsis(genreNames, maxChars, {
    separator: ' | ',
    transform: (text) => text?.toUpperCase(),
  });
};

/**
 * Smart profession formatting for profile cards
 * @param {Array} professions - Array of profession objects with displayStatus and isPrimary
 * @param {number} maxChars - Maximum number of characters allowed (default: 20)
 * @returns {string} Formatted profession string with primary first
 */
export const formatProfessions = (professions = [], maxChars = 18) => {
  // Filter visible professions and sort with primary first
  const visibleProfessions = professions
    .filter(p => p.displayStatus === 2)
    .sort((a, b) => b.isPrimary - a.isPrimary)
    .map(p => p.profession?.name || p.name) // Handle both nested (profession.name) and flat (name) structures which is the case in agency roster display get connections api
    .filter(Boolean);

  return formatTextWithEllipsis(visibleProfessions, maxChars, {
    separator: ', ',
    transform: (text) => text, // Keep original case
  });
};

/**
 * Smart employment formatting for profile cards
 * @param {Array} organizations - Array of organization objects
 * @param {number} maxChars - Maximum number of characters allowed (default: 35 for 2-line display)
 * @returns {string} Formatted employment string with current organizations as "designation, company"
 */
export const formatEmployment = (organizations = [], maxChars = 35) => {
  // Filter current employments (not past) and visible roles
  const currentEmployments = organizations
    .filter(org => !org.employee?.isPast)
    .map(org => {
      const companyName = org.organization?.name;
      const roles = org.employee?.roles || [];
      
      // Get the first/primary role that is visible (displayStatus === 2)
      const visibleRole = roles.find(role => role.displayStatus === 2);
      
      if (visibleRole && companyName) {
        const designation = visibleRole.designation?.name || visibleRole.department?.name;
        
        if (designation) {
          return `${designation}, ${companyName}`;
        } else {
          return companyName; // Fallback to just company name
        }
      }
      return null;
    })
    .filter(Boolean);

  return formatTextWithEllipsis(currentEmployments, maxChars, {
    separator: ' | ',
    transform: (text) => text, // Keep original case
  });
};

